import { middlewareGateway } from '~/gateway/middleware.gateway'
import { PAYMENT_METHOD_IDS } from '~/config/payment'
import type { ShopperBasketsTypes, ShopperOrdersTypes } from 'commerce-sdk-isomorphic'
import { PrePaymentCheckResponse, RemoveResponse } from '~/types/Givex'

const totalGCRedeemAmount = (paymentInstruments: ShopperBasketsTypes.OrderPaymentInstrument[]) => {
  if (paymentInstruments) {
    const giftCards = paymentInstruments?.filter((card) => card?.paymentMethodId === PAYMENT_METHOD_IDS.givex)

    const totalRedeemAmount = giftCards.reduce((total, card) => {
      const redeemAmount = card?.amount || 0
      return total + redeemAmount
    }, 0)

    return totalRedeemAmount
  }
  return 0
}

export const calculateOrderTotalRemainder = (
  paymentInstruments: ShopperBasketsTypes.OrderPaymentInstrument[],
  orderTotal: ShopperBasketsTypes.Basket['orderTotal'],
) => {
  const totalRedeemAmount = totalGCRedeemAmount(paymentInstruments)
  const orderTotalValue = orderTotal || 0

  const remainder = orderTotalValue - totalRedeemAmount
  return remainder
}

const calculateRedeemAndRemainingBalance = (giftCardBalance: number, orderTotalRemainder: number) => {
  const redeemAmount = Math.min(giftCardBalance, orderTotalRemainder)
  const remainingBalance = giftCardBalance - redeemAmount

  return {
    redeemAmount: redeemAmount > 0 ? redeemAmount : 0,
    remainingBalance: remainingBalance > 0 ? remainingBalance : 0,
  }
}

export default function useGivex() {
  const checkBalance = async (cardNumber: string, pin: string) => {
    const [balanceCheckError, balanceCheckResponse] = await tryCatch(
      middlewareGateway<CheckBalanceResponse>('/next-gen/givex/check-balance', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardNumber,
          pin,
        }),
        method: 'POST',
      }),
    )

    if (balanceCheckError) {
      console.error('useGivex/checkBalance/middlewareGateway', balanceCheckError)
      return null
    }
    return balanceCheckResponse
  }

  const addCard = async (cardNumber: string, pin: string, availableAmount: number) => {
    const basketsStore = useBasketsStore()
    const basket = basketsStore.basket
    const basketPIs = basket.paymentInstruments || []
    const totalAddedGiftCard = basketPIs.filter(
      (pi: ShopperBasketsTypes.OrderPaymentInstrument) => pi.paymentMethodId === PAYMENT_METHOD_IDS.givex,
    ).length

    if (totalAddedGiftCard >= 2) {
      return {
        error_message: 'checkout.givex.error_message.reach_maximum',
      }
    }

    const orderTotalRemainder = calculateOrderTotalRemainder(basketPIs, basket.orderTotal)

    if (orderTotalRemainder <= 0) {
      return {
        error_message: 'checkout.givex.error_message.no_additional_required',
      }
    }

    const { redeemAmount } = calculateRedeemAndRemainingBalance(availableAmount, orderTotalRemainder)

    if (availableAmount > 0) {
      const [authorizeError, authorizeResponse] = await tryCatch(
        middlewareGateway<AuthorizeResponse>('/next-gen/givex/authorize', {
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            basketId: basket.basketId,
            cardNumber,
            pin,
            amount: redeemAmount,
          }),
          method: 'POST',
        }),
      )

      if (authorizeError) {
        console.error('useGivex/addCard/authorize', authorizeError)
        return {
          result: 'error',
          error_message: 'checkout.givex.error_message.add_card',
        }
      }

      // refresh basket after middleware made changes to add gift card
      await basketsStore.init()
      return {
        result: authorizeResponse.result,
      }
    }
  }

  const removeCard = async (paymentInstrumentId: string) => {
    const basketsStore = useBasketsStore()

    const [removeCardError, _removeCardResponse] = await tryCatch(
      middlewareGateway<RemoveResponse>('/next-gen/givex/remove', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          basketId: basketsStore.basket.basketId,
          paymentInstrumentId: paymentInstrumentId,
        }),
        method: 'POST',
      }),
    )

    if (removeCardError) {
      console.error('useGivex/removeCard/middlewareGateway', removeCardError)
      return false
    }

    // refresh basket after middleware made changes to remove gift card
    await basketsStore.init()
    return true
  }

  const removeAllGivexCards = async () => {
    const basketsStore = useBasketsStore()
    const basketPIs = basketsStore.basket.paymentInstruments || []
    const giftCardPIs = basketPIs.filter(
      (pi: ShopperBasketsTypes.OrderPaymentInstrument) => pi.paymentMethodId === PAYMENT_METHOD_IDS.givex,
    )
    for (const pi of giftCardPIs) {
      await removeCard(pi.paymentInstrumentId || '')
    }
  }

  const placeGiftCardOrder = async () => {
    const basketsStore = useBasketsStore()
    const { createOrder, failOrder } = useOrder()
    const locale = useNuxtApp().$i18n.locale.value
    const basket = basketsStore.basket

    if (!basket) {
      console.error('useGivex/placeGiftCardOrder: No basket found')
      return null
    }
    const order = await createOrder(basket.basketId)

    // 'message' in orderto check if order is an error
    if (!order || 'message' in order || !order.orderNo) {
      return null
    }

    const [err, updatedOrder] = await tryCatch(
      middlewareGateway<ShopperOrdersTypes.Order>('/next-gen/givex/capture', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderNo: order.orderNo,
          locale: localeConfig[locale].code,
        }),
        method: 'POST',
      }),
    )
    if (err) {
      console.error('useGivex/placeGiftCardOrder/capture', err)
      await failOrder(order.orderNo)
      return basketsStore.basket
    }

    if (updatedOrder.status === 'created') {
      // The significance to 'created' means that all payment instruments did not process so status was not moved.
      await failOrder(updatedOrder.orderNo)
      return basketsStore.basket
    }

    return updatedOrder
  }

  const process = async (orderNo: string) => {
    const locale = useNuxtApp().$i18n.locale.value
    const { getOrder } = useOrder()
    const order = await getOrder(orderNo)
    if (order instanceof Error) {
      console.error('useGivex/process/getOrder', order)
      throw order
    }
    const givexPIs = order.paymentInstruments.filter((pi) => pi.paymentMethodId === PAYMENT_METHOD_IDS.givex)
    if (givexPIs.length === 0) {
      return order
    }

    const [err, updatedOrder] = await tryCatch(
      middlewareGateway<ShopperOrdersTypes.Order>('/next-gen/givex/capture', {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderNo,
          locale: localeConfig[locale].code,
        }),
        method: 'POST',
      }),
    )
    if (err) {
      console.error('useGivex/placeGiftCardOrder/capture', err)
      // fail order will be handled in the calling function
      throw err
    }
    return updatedOrder
  }

  const prePaymentCheck = async (basketId: string) => {
    const locale = useNuxtApp().$i18n.locale.value
    const queryParams = new URLSearchParams({
      basketId,
      locale: localeConfig[locale].code,
    })
    const [checkError, checkResponse] = await tryCatch(
      middlewareGateway<PrePaymentCheckResponse>(`/next-gen/givex/pre-payment-check?${queryParams.toString()}`, {
        method: 'GET',
      }),
    )
    if (checkError) {
      console.error('useGivex/prePaymentCheck/middlewareGateway', checkError)
      return false
    }
    return checkResponse?.result || false
  }

  return {
    checkBalance,
    addCard,
    removeCard,
    removeAllGivexCards,
    placeGiftCardOrder,
    process,
    prePaymentCheck,
  }
}
